import { useEffect, useState } from 'react';
import { ThumbsUp, ThumbsDown, Copy, Check, RefreshCw } from 'lucide-react';
import type { BriefingItem, BriefingItemPriority, UserRole } from '@greeniq/shared';
import {
  getTodayBriefing,
  regenerateBriefing,
  sendBriefingFeedback,
  type BriefingFeedbackRating,
} from '../api/briefings.js';
import { useMe } from '../context/MeContext.js';
import { DataCard } from './ui/data-card';
import { Badge, type BadgeProps } from './ui/badge';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

interface Props {
  courseSlug: string;
}

const REGEN_ROLES: UserRole[] = ['ageye_admin', 'superintendent'];

const PRIORITY_META: Record<
  BriefingItemPriority,
  { label: string; variant: BadgeProps['variant'] }
> = {
  P1: { label: 'P1 · today', variant: 'destructive' },
  P2: { label: 'P2 · this week', variant: 'warning' },
  P3: { label: 'P3 · awareness', variant: 'default' },
};

const CATEGORY_LABEL: Record<string, string> = {
  disease: 'Disease',
  irrigation: 'Irrigation',
  fertility: 'Fertility',
  weather: 'Weather',
  satellite: 'Satellite',
  scouting: 'Scouting',
  operations: 'Ops',
};

export function BriefingCard({ courseSlug }: Props) {
  const { me } = useMe();
  const [headline, setHeadline] = useState<string | null>(null);
  const [forDate, setForDate] = useState<string | null>(null);
  const [items, setItems] = useState<BriefingItem[] | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenMessage, setRegenMessage] = useState<string | null>(null);

  const role: UserRole | undefined = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const canRegen = role ? REGEN_ROLES.includes(role) : false;

  async function load() {
    try {
      const resp = await getTodayBriefing(courseSlug);
      if (resp.briefing) {
        setHeadline(resp.briefing.headline);
        setForDate(resp.briefing.forDate);
      } else {
        setHeadline(null);
        setForDate(null);
      }
      setItems(resp.items);
    } catch {
      // hidden when error
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug]);

  async function onRegen() {
    setRegenerating(true);
    setRegenMessage(null);
    try {
      await regenerateBriefing(courseSlug);
      setRegenMessage('Regenerating — refresh in 20-30s for the new output.');
      setTimeout(() => {
        void load();
        setRegenMessage(null);
      }, 25_000);
    } catch (err) {
      setRegenMessage(`Regen failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRegenerating(false);
    }
  }

  if (!items) return null;

  if (items.length === 0 && !headline) {
    return (
      <DataCard
        className="mt-6 border-dashed"
        title={<span className="uppercase tracking-[0.12em] text-fg-secondary text-xs">Morning briefing</span>}
        actions={
          canRegen && (
            <Button size="sm" onClick={() => void onRegen()} disabled={regenerating}>
              <RefreshCw size={14} className={regenerating ? 'animate-spin' : ''} />
              {regenerating ? 'Queuing…' : 'Generate now'}
            </Button>
          )
        }
      >
        <p className="text-sm text-fg-muted">
          No briefing generated for today yet. The hourly cron fires one per course at local 4:00 am.
        </p>
        {regenMessage && <p className="mt-2 text-xs text-greeniq-400">{regenMessage}</p>}
      </DataCard>
    );
  }

  return (
    <DataCard
      className="mt-6"
      glow
      title="Morning briefing"
      subtitle={forDate ?? undefined}
      actions={
        canRegen && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void onRegen()}
            disabled={regenerating}
            className="text-greeniq-400 hover:text-greeniq-300"
          >
            <RefreshCw size={14} className={regenerating ? 'animate-spin' : ''} />
            {regenerating ? 'Queuing…' : 'Regenerate'}
          </Button>
        )
      }
    >
      {headline && (
        <p className="mb-3 font-display text-base font-semibold leading-snug tracking-tight text-fg md:text-lg break-words">
          {headline}
        </p>
      )}
      {regenMessage && <p className="mb-3 text-xs text-greeniq-400">{regenMessage}</p>}
      <ul className="space-y-3">
        {items.map((item) => (
          <BriefingItemView key={item.id} item={item} courseSlug={courseSlug} />
        ))}
      </ul>
    </DataCard>
  );
}

function itemAsText(item: BriefingItem): string {
  const lines = [`[${item.priority}] ${item.title}`, item.detail];
  if (item.action) lines.push(`→ ${item.action}`);
  if (item.evidence && item.evidence.length > 0) {
    lines.push(`Evidence: ${item.evidence.join('; ')}`);
  }
  return lines.join('\n');
}

function BriefingItemView({
  item,
  courseSlug,
}: {
  item: BriefingItem;
  courseSlug: string;
}) {
  const meta = PRIORITY_META[item.priority] ?? PRIORITY_META.P3;
  const category = CATEGORY_LABEL[item.category] ?? item.category;
  const [myRating, setMyRating] = useState<BriefingFeedbackRating | null>(null);
  const [copyTick, setCopyTick] = useState(false);

  async function rate(rating: BriefingFeedbackRating) {
    const prev = myRating;
    setMyRating(rating);
    try {
      await sendBriefingFeedback(courseSlug, item.id, rating);
    } catch {
      setMyRating(prev);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(itemAsText(item));
      setCopyTick(true);
      setTimeout(() => setCopyTick(false), 1500);
    } catch {
      /* non-secure context or old browser — ignore */
    }
  }

  return (
    <li className="rounded-sm border border-border bg-bg-elevated p-3">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Badge variant={meta.variant}>{meta.label}</Badge>
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-muted">
          {category}
        </span>
      </div>
      <div className="text-sm font-semibold text-fg">{item.title}</div>
      <p className="mt-1 text-sm text-fg-secondary">{item.detail}</p>
      {item.action && (
        <p className="mt-2 text-sm font-semibold text-greeniq-400">→ {item.action}</p>
      )}
      {item.evidence && item.evidence.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {item.evidence.map((e, i) => (
            <Badge key={i} variant="secondary" className="normal-case tracking-normal font-normal">
              {e}
            </Badge>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <Button
          type="button"
          size="icon"
          variant={myRating === 'up' ? 'default' : 'outline'}
          onClick={() => void rate('up')}
          aria-pressed={myRating === 'up'}
          aria-label="Mark item as useful"
          className={cn('size-9', myRating === 'up' && 'shadow-glow-green')}
        >
          <ThumbsUp size={14} />
        </Button>
        <Button
          type="button"
          size="icon"
          variant={myRating === 'down' ? 'destructive' : 'outline'}
          onClick={() => void rate('down')}
          aria-pressed={myRating === 'down'}
          aria-label="Mark item as not useful"
          className="size-9"
        >
          <ThumbsDown size={14} />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => void copy()}
          aria-label="Copy item to clipboard"
          className="ml-auto"
        >
          {copyTick ? <Check size={14} /> : <Copy size={14} />}
          {copyTick ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </li>
  );
}
